import {FormControl, FormField, Tooltip} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {not} from 'ramda';
import {isFalse} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useGetVatRatesOptions} from '@dms/shared';
import {FALLBACK_HANDLING_UNIT} from '@dms/warehouse/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {ReceiveNoteItemFormType} from './types/ReceiveNoteItemDetailSchema';

export interface ReceiveNoteItemFormProps extends RequiredTestIdProps {
  control: FormControl<ReceiveNoteItemFormType>;
  handlingUnit: string | Nullish;
  currency: string;
  isChecked: boolean;
  isEditingDisabled: boolean;
  isFromReceiveNote?: boolean;
}

export function ReceiveNoteItemForm(props: ReceiveNoteItemFormProps) {
  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const isDisabled =
    not(props.isChecked) || props.isEditingDisabled || isFalse(props.isFromReceiveNote);

  const getTooltipLabel = () => {
    if (not(props.isChecked)) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledItemNotChecked');
    }
    if (props.isEditingDisabled) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledReceiveNoteCompleted');
    }
    if (isFalse(props.isFromReceiveNote)) {
      return i18n.t('entity.warehouse.tooltips.fieldDisabledNotFromReceiveNote');
    }
    return '';
  };

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.receiveNoteItem')}</Heading>
      <Grid columns={4}>
        <GridItem span={1}>
          <Tooltip label={getTooltipLabel()} isDisabled={!isDisabled}>
            <FormField
              type="number"
              name="receiveNoteItem.quantity"
              label={i18n.t('general.labels.quantity')}
              control={props.control}
              isDisabled={isDisabled}
              decimalPlaces={4}
              suffix={props.handlingUnit ?? FALLBACK_HANDLING_UNIT}
              data-testid={suffixTestId('inputs.quantity', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getTooltipLabel()} isDisabled={!isDisabled}>
            <FormField
              control={props.control}
              type="number"
              name="receiveNoteItem.unitPrice"
              label={i18n.t('entity.warehouse.labels.unitPrice')}
              isDisabled={isDisabled}
              suffix={props.currency}
              decimalPlaces={2}
              data-testid={suffixTestId('inputs.unitPrice', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getTooltipLabel()} isDisabled={!isDisabled}>
            <FormField
              control={props.control}
              type="number"
              name="receiveNoteItem.totalPrice"
              label={i18n.t('general.labels.totalPrice')}
              isDisabled={isDisabled}
              suffix={props.currency}
              decimalPlaces={2}
              data-testid={suffixTestId('inputs.totalPrice', props)}
            />
          </Tooltip>
        </GridItem>
        <GridItem span={1}>
          <Tooltip label={getTooltipLabel()} isDisabled={!isDisabled}>
            <FormField
              type="choice"
              name="receiveNoteItem.vatType"
              label={i18n.t('general.labels.vat')}
              options={vatOptions}
              control={props.control}
              isNotClearable
              isDisabled={isDisabled}
              menuInPortal
              data-testid={suffixTestId('inputs.vatType', props)}
            />
          </Tooltip>
        </GridItem>
      </Grid>
    </VStack>
  );
}
